import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import DatePicker, { registerLocale } from 'react-datepicker'
import es from 'date-fns/locale/es'
import 'react-datepicker/dist/react-datepicker.css'
import { connect } from 'react-redux'
import Modal from '../../../components/ui/modal'
import { ChevronLeft, UserAvatar } from '../../../assets/icons'
import { Button } from '../../../components/ui/button'
import useIsMobile from '../../../hooks/useIsMobile'
import { capitalizeEachWord } from '../../../utils/stringsUtils'
import { BOOKING_TYPES } from '../constants'
import RecommendationCard from './RecommendationCard'
import { formatDayMonth, formatHourMinute, toDateOnly } from '../../../utils/formatDate'
import { getNearestTurno } from '../../../utils/groupTurnosByProfessional'

registerLocale('es', es)

const getTurnSpecialtyKey = turn =>
  turn?.idEspecialidad ?? turn?.especialidad ?? turn?.speciality ?? null

const getSpecialtiesToEvaluate = submittedFilters => {
  if (Array.isArray(submittedFilters?.specialities) && submittedFilters.specialities.length > 0) {
    return submittedFilters.specialities
  }

  return submittedFilters?.speciality ? [submittedFilters.speciality] : []
}

const getFirstCommonDate = (allTurnos, submittedFilters) => {
  if (!Array.isArray(allTurnos) || allTurnos.length === 0) return null

  const searchedSpecialties = getSpecialtiesToEvaluate(submittedFilters).map(String)
  const datesBySpecialty = new Map()

  allTurnos.forEach(turno => {
    const specialtyKey = getTurnSpecialtyKey(turno)
    if (specialtyKey == null || !turno?.fechaTurnoDateTime) return

    const normalizedSpecialtyKey = String(specialtyKey)
    const dayKey = toDateOnly(turno.fechaTurnoDateTime).getTime()

    if (!datesBySpecialty.has(normalizedSpecialtyKey)) {
      datesBySpecialty.set(normalizedSpecialtyKey, new Set())
    }

    datesBySpecialty.get(normalizedSpecialtyKey).add(dayKey)
  })

  const specialtyKeys =
    searchedSpecialties.length > 0 ? searchedSpecialties : Array.from(datesBySpecialty.keys())

  if (specialtyKeys.length === 0) return null

  if (specialtyKeys.some(key => !datesBySpecialty.has(String(key)))) {
    return null
  }

  const sets = specialtyKeys.map(key => datesBySpecialty.get(String(key)))
  const baseSet = sets.reduce((smallest, current) =>
    current.size < smallest.size ? current : smallest
  )

  let firstCommonDayKey = null

  for (const dayKey of baseSet) {
    if (specialtyKeys.every(specialtyKey => datesBySpecialty.get(String(specialtyKey)).has(dayKey))) {
      if (firstCommonDayKey === null || dayKey < firstCommonDayKey) {
        firstCommonDayKey = dayKey
      }
    }
  }

  return firstCommonDayKey === null ? null : new Date(firstCommonDayKey)
}

const SelectAppointment = ({
  turnosDisponibles = [],
  allTurnos = [],
  submittedFilters,
  bookingMode = BOOKING_TYPES.SINGLE,
  selectedTurn,
  clinics = [],
  onBack,
  onConfirm
}) => {
  const isMobile = useIsMobile()
  const isMultiple = bookingMode === BOOKING_TYPES.MULTIPLE
  const turnos = turnosDisponibles

  const nearest = useMemo(() => getNearestTurno(turnos), [turnos])
  const selectedBaseTurn = useMemo(() => {
    if (selectedTurn && turnos.some(turno => turno.idTurno === selectedTurn.idTurno)) {
      return selectedTurn
    }

    return nearest
  }, [selectedTurn, turnos, nearest])

  const [selectedDate, setSelectedDate] = useState(
    selectedBaseTurn ? new Date(selectedBaseTurn.fechaTurnoDateTime) : null
  )
  const [selectedTurnoId, setSelectedTurnoId] = useState(selectedBaseTurn?.idTurno || null)
  const [showContinueAsSingleModal, setShowContinueAsSingleModal] = useState(false)

  useEffect(() => {
    if (selectedBaseTurn) {
      setSelectedDate(new Date(selectedBaseTurn.fechaTurnoDateTime))
      setSelectedTurnoId(selectedBaseTurn.idTurno)
      return
    }

    setSelectedDate(null)
    setSelectedTurnoId(null)
  }, [selectedBaseTurn])

  const availableDates = useMemo(() => {
    const uniq = new Map()
    turnos.forEach(t => {
      const day = toDateOnly(t.fechaTurnoDateTime).getTime()
      uniq.set(day, new Date(day))
    })

    return Array.from(uniq.values()).sort((a, b) => a - b)
  }, [turnos])

  const turnosByDate = useMemo(() => {
    if (!selectedDate) return []

    const dayKey = toDateOnly(selectedDate).getTime()
    return turnos
      .filter(t => toDateOnly(t.fechaTurnoDateTime).getTime() === dayKey)
      .sort((a, b) => new Date(a.fechaTurnoDateTime) - new Date(b.fechaTurnoDateTime))
  }, [selectedDate, turnos])

  const handleSelectDate = date => {
    setSelectedDate(date)
    setSelectedTurnoId(null)
  }

  const handleSelectHour = turnoId => {
    setSelectedTurnoId(turnoId)
  }

  const getSelectedTurn = () => {
    if (!selectedTurnoId) return null
    return turnos.find(t => t.idTurno === selectedTurnoId)
  }

  const handleContinue = () => {
    const selected = getSelectedTurn()
    if (!selected) return

    if (isMultiple) {
      onConfirm(selected, { addToCart: true, continueToConfirm: false })
      return
    }

    onConfirm(selected, { continueToConfirm: true })
  }

  const handleContinueAsSingle = () => {
    const selected = getSelectedTurn()
    if (!selected) return
    onConfirm(selected, { addToCart: false, continueToConfirm: true })
  }

  const recommendedDateLabel = useMemo(() => {
    const firstCommonDate = getFirstCommonDate(allTurnos, submittedFilters)
    return firstCommonDate ? formatDayMonth(firstCommonDate) : ''
  }, [allTurnos, submittedFilters])

  const clinicName = useMemo(() => {
    const code = nearest?.clinica
    if (code == null || code === '') return ''
    const match = clinics.find(
      c => String(c.value) === String(code) || String(c.codigoEmpresa) === String(code)
    )
    return match?.label || capitalizeEachWord(String(code))
  }, [clinics, nearest])

  return (
    <Container>
      <Header>
        {!isMobile && (
          <BackTitle m={'0'} onClick={onBack}>
            <ChevronLeft size={8} /> Volver
          </BackTitle>
        )}
        <Title>Agendar turno</Title>
      </Header>

      {isMultiple && <RecommendationCard dateLabel={recommendedDateLabel} />}

      <DoctorCard>
        <Avatar>
          <UserAvatar color="#161616" />
        </Avatar>
        <div>
          <DoctorName>{capitalizeEachWord(nearest?.nombreProfesional || '')}</DoctorName>
          <DoctorMeta>
            {capitalizeEachWord(nearest?.nombreEspecialidad || '')} -{' '}
            {clinicName}
          </DoctorMeta>
        </div>
      </DoctorCard>

      <Grid>
        <CalendarBlock>
          <SectionTitle>Fecha</SectionTitle>
          <DatePicker
            selected={selectedDate}
            onChange={handleSelectDate}
            includeDates={availableDates}
            disabledKeyboardNavigation
            locale="es"
            dateFormat="dd/MM/yyyy"
            inline
          />
        </CalendarBlock>

        <HoursBlock>
          <SectionTitle>Hora</SectionTitle>
          <HoursGrid>
            {turnosByDate.map(t => (
              <HourButton
                key={t.idTurno}
                type="button"
                onClick={() => handleSelectHour(t.idTurno)}
                $active={t.idTurno === selectedTurnoId}
              >
                {formatHourMinute(t.fechaTurnoDateTime)} hs
              </HourButton>
            ))}
            {turnosByDate.length === 0 && <EmptyText>No hay horarios para esta fecha</EmptyText>}
          </HoursGrid>
        </HoursBlock>
      </Grid>

      <Actions>
        <Button
          type="button"
          $variant="primary"
          disabled={!selectedTurnoId}
          onClick={handleContinue}
          label={isMultiple ? 'Agregar turno' : 'Continuar'}
          size={isMobile ? 'mobile' : 'xlarge'}
        />

        {isMultiple && (
          <Button
            type="button"
            $variant="tertiary"
            disabled={!selectedTurnoId}
            onClick={() => setShowContinueAsSingleModal(true)}
            label={'Continuar con turno individual'}
            size={isMobile ? 'mobile' : 'xlarge'}
          />
        )}
      </Actions>

      {showContinueAsSingleModal && (
        <Modal onClose={() => setShowContinueAsSingleModal(false)} isFullHeight={isMobile}>
          <ConfirmModalBody>
            <ConfirmTitle>¿Estás seguro que querés continuar con un turno individual?</ConfirmTitle>
            <ConfirmText>
              Perderás tu búsqueda actual y turnos previamente seleccionados.
            </ConfirmText>
            <ConfirmActions>
              <Button
                label="Sí, continuar"
                $variant="primary"
                size="mobile"
                onClick={() => {
                  setShowContinueAsSingleModal(false)
                  handleContinueAsSingle()
                }}
              />
              <Button
                label="Volver"
                $variant="secondary"
                size="mobile"
                onClick={() => setShowContinueAsSingleModal(false)}
              />
            </ConfirmActions>
          </ConfirmModalBody>
        </Modal>
      )}
    </Container>
  )
}

function mapStateToProps({ appointment }, ownProps) {
  const booking = appointment?.booking || {}

  return {
    turnosDisponibles:
      booking.selectedAppointments && booking.selectedAppointments.length > 0
        ? booking.selectedAppointments
        : ownProps.turnosDisponibles || [],
    allTurnos: booking.searchResults || ownProps.allTurnos || [],
    submittedFilters: booking.submittedFilters || ownProps.submittedFilters || {},
    selectedTurn: booking.selectedTurn || ownProps.selectedTurn,
    clinics: (booking.catalogs && booking.catalogs.clinics) || ownProps.clinics || [],
    bookingMode: booking.mode || ownProps.bookingMode || BOOKING_TYPES.SINGLE
  }
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 80vh;
  width: 80vw;
  padding: 8px 12px 16px 12px;

  @media (max-width: ${props => props.theme.sizes.breakPoints.small}) {
    min-height: 100%;
    width: 100%;
  }
`

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  gap: 6px;
`

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  margin: 24px 0;
`

const DoctorCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border: 1px solid ${props => props.theme.colors.greys.dividerGray};
  border-radius: 10px;
  background: ${props => props.theme.colors.brand.white};
  max-width: 27%;
  min-width: 325px;

  @media (max-width: ${props => props.theme.sizes.breakPoints.small}) {
    max-width: 100%;
  }
`

const Avatar = styled.div`
  width: 32px;
  height: 32px;
  display: grid;
  place-items: center;
  border: 1px solid ${props => props.theme.colors.greys.cardHoverBorder};
  border-radius: 50%;
  font-size: 16px;
`

const DoctorName = styled.div`
  font-weight: 600;
  font-size: 16px;
  color: ${props => props.theme.colors.text.primaryText};
`

const DoctorMeta = styled.div`
  font-size: 14px;
  color: ${props => props.theme.colors.text.descriptionText};
`

const Grid = styled.div`
  display: flex;
  gap: 20px;

  @media (max-width: ${props => props.theme.sizes.breakPoints.small}) {
    display: grid;
    grid-template-columns: 1fr;
    gap: 20px;
    align-items: flex-start;
  }
`

export const CalendarBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 27%;
  min-width: 325px;

  @media (max-width: ${props => props.theme.sizes.breakPoints.small}) {
    width: 100%;
    min-width: 0;
  }

  .react-datepicker {
    display: block;
    width: 100%;
    border: 1px solid #e0e0e0;
    border-radius: 12px;
    font-family: inherit;
  }

  .react-datepicker__month-container {
    float: none;
    width: 100%;
  }

  .react-datepicker-wrapper {
    width: 100%;
  }

  .react-datepicker__header {
    background-color: white;
    border-bottom: none;
    border-radius: 12px 12px 0 0;
    padding: 12px;
  }

  .react-datepicker__current-month {
    font-size: 18px;
    padding-left: 24px;
    font-weight: 600;
    color: #161616;
    width: 100%;
    text-align: left;
    text-transform: capitalize;
  }

  .react-datepicker__navigation-icon {
    width: 6px;
    height: 6px;
  }

  .react-datepicker__navigation--previous {
    left: auto;
    right: 42px;
  }

  .react-datepicker__navigation-next {
    overflow: visible;
    width: 10px;
    right: 14px;
  }

  .react-datepicker__day-names {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 2px;
    padding: 8px 12px 4px 12px;
    margin: 0;
    background: #fff;
  }

  .react-datepicker__day-name {
    font-size: 12px;
    font-weight: 600;
    color: #888;
    text-align: center;
    padding: 4px 0;
  }

  .react-datepicker__day-name,
  .react-datepicker__day {
    width: 100%;
    margin: 0;
    box-sizing: border-box;
  }

  .react-datepicker__month {
    margin: 0;
    display: grid;
    gap: 2px;
    padding: 0 12px 12px 12px;
  }

  .react-datepicker__week {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 2px;
    margin: 0;
  }

  .react-datepicker__day {
    aspect-ratio: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    color: #333;
    border: none;
    border-radius: 6px;
    background: #fff;
    cursor: pointer;
    transition: all 0.2s ease;
    padding: 0;

    &:hover:not(.react-datepicker__day--outside-month) {
      background: #f0f0f0;
    }
  }

  .react-datepicker__day--selected {
    background-color: #003da5;
    color: #fff;
    font-weight: 600;
    border-radius: 50%;

    &:hover {
      background-color: #003da5;
    }
  }

  .react-datepicker__day--keyboard-selected {
    background-color: transparent;
    color: #333;
    border-radius: 6px;
  }

  .react-datepicker__day--keyboard-selected.react-datepicker__day--selected {
    background-color: #003da5;
    color: #fff;
    border-radius: 50%;
  }

  .react-datepicker__day--outside-month {
    color: #ccc;
    cursor: not-allowed;
  }

  .react-datepicker__day--disabled {
    color: #ccc;
    cursor: not-allowed;
  }
`

const HoursBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`

const SectionTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.theme.colors.text.primaryText};
`

const HoursGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
  gap: 8px;

  @media (max-width: ${props => props.theme.sizes.breakPoints.small}) {
    grid-template-columns: repeat(3, 1fr);
  }
`

const HourButton = styled.button`
  padding: 10px 12px;
  border: 1px solid ${props => (props.$active ? props.theme.colors.brand.blueOmint : props.theme.colors.greys.dividerGray)};
  background: ${props => (props.$active ? props.theme.colors.brand.blueOmint : props.theme.colors.brand.white)};
  color: ${props => (props.$active ? props.theme.colors.brand.white : props.theme.colors.text.titleText)};
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${props => props.theme.colors.brand.blueOmint};
  }
`

const EmptyText = styled.div`
  grid-column: 1 / -1;
  text-align: center;
  color: ${props => props.theme.colors.text.mutedText};
  padding: 16px;
  font-size: 14px;
  margin: 0;
`

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 20px;
  width: 100%;
  max-width: 360px;

  @media (max-width: ${props => props.theme.sizes.breakPoints.small}) {
    max-width: 100%;
  }
`

const ConfirmModalBody = styled.div`
  width: 100%;
  max-width: 420px;
  padding: 6px;
`

const ConfirmTitle = styled.h3`
  margin: 0 0 12px 0;
  color: ${props => props.theme.colors.text.primaryText};
  font-size: 20px;
`

const ConfirmText = styled.p`
  margin: 0;
  color: ${props => props.theme.colors.text.secondaryText};
  font-size: 14px;
`

const ConfirmActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 16px;
`

export const BackTitle = styled.p`
  color: ${props => props.theme.colors.brand.blueOmint};
  text-decoration: underline;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  margin: ${({ m }) => m || '0 0 24px 0'};

  &:hover {
    text-decoration: underline;
  }
`

export default connect(mapStateToProps)(SelectAppointment)
